import {
  FeatureToggles,
  SiteSettings,
} from '@silvertours/common-landingpages-view';

type Weekday = 'always' | 'workdays' | 'weekends' | 'saturday' | 'sunday';

type OpenCloseTimes = {
  open: string;
  close: string;
};

type OpenHoursCommon = Partial<Record<Weekday, OpenCloseTimes>>;

type OpenHoursExceptions = Record<string, OpenCloseTimes | null>;

type InfolineTimes = {
  common: OpenHoursCommon;
  exceptions: OpenHoursExceptions | [];
};

/**
 * Feature toggles which can be dynamically updated, for instance by A/B tests.
 * To set a toggle, the following snippet can be used:
 *
 * ```js
 * window.featureToggles = window.featureToggles || [];
 * window.featureToggles.push(['myTest', true]);
 * ```
 *
 * This always works, regardless of whether the client app is already loaded or not.
 */
export const DYNAMIC_FEATURE_TOGGLES: (keyof FeatureToggles)[] = [
  'defaultSortRecommender',
  'fetchOffersFromSearchApi',
  'hideAppInstallBanner',
  'isSocialLoginActive',
];

type SiteInfo = {
  settings: SiteSettings;
  featureToggles: FeatureToggles;
  /**
   * Only the dynamic toggles that the page got from a push or from the URL. A
   * missing key means that no value arrived yet. Use this to tell "not yet
   * assigned" apart from `false`.
   */
  dynamicFeatureToggles: Partial<Record<keyof FeatureToggles, boolean>>;
  abTests: Record<string, string | undefined>;
  isAbTestReady: boolean;
  openingTimes: InfolineTimes;
  settingsLoaded: boolean;
  /**
   * The currency the user has selected in the regional settings. Falls back to
   * the site's default currency (`settings.selectedCurrency`) until a choice is
   * made or restored from the persistence cookie.
   */
  selectedCurrency: string;
  setSelectedCurrency: (currency: string) => void;
};

export type {
  OutputCurrency,
  SessionData,
  SocialLoginSettings,
} from '@silvertours/common-landingpages-view';
export type { FeatureToggles, InfolineTimes, SiteInfo, SiteSettings };
